import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import LanguageToggle from "@/components/LanguageToggle";
import ReferralExtras, { ReferralStrip } from "@/components/ReferralExtras";
import {
  LayoutDashboard, CalendarPlus, FileBarChart, User, CalendarDays, CalendarRange,
  Users, IndianRupee, UtensilsCrossed, FileText, LogOut, ShieldCheck, MessageSquare,
  BadgeIndianRupee, CreditCard, Settings, Gift, BarChart3, Smartphone, QrCode, Menu, X, Phone, Languages, Wrench,
} from "lucide-react";

const userNav = (t) => [
  { to: "/app", label: t("dashboard"), icon: LayoutDashboard, key: "home" },
  { to: "/app/entry", label: t("daily_entry"), icon: CalendarPlus, key: "entry" },
  { to: "/app/bulk", label: t("bulk_entry"), icon: CalendarRange, key: "bulk" },
  { to: "/app/reports", label: t("reports"), icon: FileBarChart, key: "reports" },
  { to: "/app/history", label: t("history"), icon: CalendarDays, key: "history" },
  { to: "/app/calculators", label: t("my_calculators"), icon: Wrench, key: "calculators", badge: "NEW" },
  { to: "/app/refer", label: t("refer_earn"), icon: Gift, key: "refer", badge: "FREE" },
  { to: "/app/plan", label: t("my_plan"), icon: BadgeIndianRupee, key: "plan" },
  { to: "/app/profile", label: t("profile"), icon: User, key: "profile" },
];

const adminNav = (t) => [
  { to: "/admin", label: t("dashboard"), icon: LayoutDashboard, key: "home" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, key: "analytics" },
  { to: "/admin/users", label: t("users"), icon: Users, key: "users" },
  { to: "/admin/subscriptions", label: t("subscriptions"), icon: BadgeIndianRupee, key: "subscriptions" },
  { to: "/admin/payments", label: t("payments"), icon: CreditCard, key: "payments" },
  { to: "/admin/qr-payments", label: "QR Payments", icon: QrCode, key: "qr" },
  { to: "/admin/referrals", label: t("refer_earn"), icon: Gift, key: "referrals" },
  { to: "/admin/recharge-claims", label: "Recharge Claims", icon: Smartphone, key: "recharge" },
  { to: "/admin/rates", label: t("rates"), icon: IndianRupee, key: "rates" },
  { to: "/admin/menus", label: t("menus"), icon: UtensilsCrossed, key: "menus" },
  { to: "/admin/content", label: t("content"), icon: FileText, key: "content" },
  { to: "/admin/settings", label: t("settings"), icon: Settings, key: "settings" },
  { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquare, key: "testimonials" },
  { to: "/admin/messages", label: "Messages", icon: MessageSquare, key: "messages" },
  { to: "/admin/profile", label: t("profile"), icon: ShieldCheck, key: "profile" },
];

// Keyboard-aware: hide the bottom bar while the on-screen keyboard is open.
function useKeyboardOpen() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const onResize = () => setOpen(vv.height < window.innerHeight * 0.75);
    vv.addEventListener("resize", onResize);
    return () => vv.removeEventListener("resize", onResize);
  }, []);
  return open;
}

export default function AppLayout({ mode }) {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const items = mode === "admin" ? adminNav(t) : userNav(t);
  const [moreOpen, setMoreOpen] = useState(false);
  const kbOpen = useKeyboardOpen();

  const doLogout = async () => { await logout(); nav("/login"); };
  const active = (to) => loc.pathname === to;

  const adminBottomKeys = ["home", "subscriptions", "payments", "users", "profile"];
  const adminBottom = adminNav(t).filter((i) => adminBottomKeys.includes(i.key));

  return (
    <div className="min-h-screen flex relative z-10">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-thali-paper border-r border-thali-turmeric/20 sticky top-0 h-screen">
        <div className="p-5 flex items-center gap-2 border-b border-thali-turmeric/20">
          <span className={`w-10 h-10 rounded-xl ${mode === "admin" ? "bg-thali-saffron shadow-btn-saffron" : "bg-thali-green shadow-btn-green"} text-white grid place-items-center`}>
            {mode === "admin" ? <ShieldCheck className="w-5 h-5" /> : <UtensilsCrossed className="w-5 h-5" />}
          </span>
          <div>
            <div className="font-heading font-extrabold text-thali-green leading-none">PM POSHAN</div>
            <div className="text-xs text-thali-ink/50">{mode === "admin" ? t("admin") : "MDM"}</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {items.map((it) => (
            <Link key={it.to} to={it.to} data-testid={`side-${it.key}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-colors ${active(it.to) ? "bg-thali-green text-white shadow-btn-green" : "text-thali-ink/70 hover:bg-thali-cream"}`}>
              <it.icon className="w-5 h-5" /> {it.label}
              {it.badge && <span className="ml-auto text-[10px] font-extrabold bg-thali-saffron text-white rounded-full px-1.5 py-0.5">{it.badge}</span>}
            </Link>
          ))}
        </nav>
        <div className="p-3 border-t border-thali-turmeric/20">
          <button onClick={doLogout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-semibold text-thali-ink/70 hover:bg-red-50 hover:text-red-600 transition-colors">
            <LogOut className="w-5 h-5" /> {t("logout")}
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-thali-paper/95 backdrop-blur border-b border-thali-turmeric/20">
          <div className="px-4 h-16 flex items-center justify-between">
            <div className="md:hidden flex items-center gap-2">
              <span className={`w-9 h-9 rounded-lg ${mode === "admin" ? "bg-thali-saffron" : "bg-thali-green"} text-white grid place-items-center`}>
                {mode === "admin" ? <ShieldCheck className="w-4 h-4" /> : <UtensilsCrossed className="w-4 h-4" />}
              </span>
              <span className="font-heading font-extrabold text-thali-green">PM POSHAN</span>
            </div>
            <div className="hidden md:block font-heading font-bold text-thali-ink/70 truncate">
              {t("welcome")}, {user?.name}
            </div>
            <div className="flex items-center gap-2">
              <LanguageToggle />
              <button onClick={doLogout} className="md:hidden p-2 text-thali-ink/70" aria-label="Logout" data-testid="logout-btn-mobile"><LogOut className="w-5 h-5" /></button>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 py-5 md:px-8 md:py-8 pb-28 md:pb-8 max-w-5xl w-full mx-auto animate-fade-in-up">
          {mode === "user" && <ReferralStrip />}
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      {mode === "admin" ? (
        <nav className={`fixed bottom-0 inset-x-0 z-40 bg-thali-paper border-t border-thali-turmeric/20 flex justify-around items-stretch md:hidden shadow-[0_-4px_16px_rgba(0,0,0,0.06)] transition-transform duration-200 ${kbOpen ? "translate-y-full" : ""}`}>
          {adminBottom.map((it) => (
            <Link key={it.to} to={it.to} data-testid={`bnav-${it.key}`}
              className={`relative flex-1 min-h-[60px] flex flex-col items-center justify-center gap-0.5 text-xs font-semibold transition-colors ${active(it.to) ? "text-thali-green" : "text-thali-ink/50"}`}>
              <it.icon className={`w-6 h-6 ${active(it.to) ? "scale-110" : ""} transition-transform`} />
              {it.label}
            </Link>
          ))}
        </nav>
      ) : (
        <UserBottomNav active={active} kbOpen={kbOpen} t={t} onMore={() => setMoreOpen(true)} />
      )}

      {mode === "user" && (
        <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} t={t} active={active} onLogout={doLogout} />
      )}
      {mode === "user" ? <ReferralExtras /> : <ReferralExtras admin />}
    </div>
  );
}

function BottomItem({ to, icon: Icon, label, active, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className={`relative flex-1 min-h-[64px] flex flex-col items-center justify-center gap-0.5 text-[11px] font-bold transition-colors ${active ? "text-thali-green" : "text-thali-ink/50"}`}>
      {active && <span className="absolute top-0 h-1 w-8 rounded-b-full bg-thali-green" />}
      <Icon className={`w-6 h-6 ${active ? "scale-110" : ""} transition-transform`} />
      {label}
    </Link>
  );
}

function UserBottomNav({ active, kbOpen, t, onMore }) {
  const bulkActive = active("/app/bulk");
  return (
    <nav data-testid="user-bottom-nav"
      className={`fixed bottom-0 inset-x-0 z-40 md:hidden transition-transform duration-200 ${kbOpen ? "translate-y-full" : ""}`}
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div className="relative bg-thali-paper border-t border-thali-turmeric/20 flex justify-around items-stretch h-16 shadow-[0_-4px_16px_rgba(0,0,0,0.08)]">
        <BottomItem to="/app" icon={LayoutDashboard} label={t("home")} active={active("/app")} testid="bnav-home" />
        <BottomItem to="/app/entry" icon={CalendarPlus} label={t("entry")} active={active("/app/entry")} testid="bnav-entry" />
        <div className="flex-1" aria-hidden />
        <BottomItem to="/app/reports" icon={FileBarChart} label={t("reports")} active={active("/app/reports")} testid="bnav-reports" />
        <button onClick={onMore} data-testid="bnav-more"
          className="relative flex-1 min-h-[64px] flex flex-col items-center justify-center gap-0.5 text-[11px] font-bold text-thali-ink/50">
          <Menu className="w-6 h-6" />
          {t("more")}
        </button>

        {/* Raised centre Bulk button */}
        <Link to="/app/bulk" data-testid="bnav-bulk" aria-label={t("bulk_entry")}
          className="absolute left-1/2 -translate-x-1/2 -top-5 flex flex-col items-center">
          <span className={`w-14 h-14 rounded-full grid place-items-center text-white shadow-btn-saffron ring-4 ring-thali-paper transition-transform active:scale-95 ${bulkActive ? "bg-thali-green" : "bg-thali-saffron"}`}>
            <CalendarRange className="w-7 h-7" />
          </span>
          <span className={`text-[11px] font-extrabold mt-0.5 ${bulkActive ? "text-thali-green" : "text-thali-saffron"}`}>{t("bulk")}</span>
        </Link>
      </div>
    </nav>
  );
}

function MoreSheet({ open, onClose, t, active, onLogout }) {
  const items = [
    { to: "/app/profile", label: t("profile"), icon: User },
    { to: "/app/calculators", label: t("my_calculators"), icon: Wrench, badge: "NEW" },
    { to: "/app/plan", label: t("my_plan"), icon: BadgeIndianRupee },
    { to: "/app/refer", label: t("refer_earn"), icon: Gift, badge: "FREE" },
    { to: "/suggestions", label: t("suggestions"), icon: MessageSquare },
    { to: "/contact", label: t("contact"), icon: Phone },
  ];
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] md:hidden" data-testid="more-sheet">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute bottom-0 inset-x-0 bg-thali-paper rounded-t-3xl shadow-2xl max-h-[60vh] overflow-y-auto animate-fade-in-up"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}>
        <div className="sticky top-0 bg-thali-paper pt-3 pb-2">
          <div className="w-12 h-1.5 bg-thali-turmeric/40 rounded-full mx-auto" />
          <button onClick={onClose} className="absolute right-4 top-3 text-thali-ink/50" aria-label="Close" data-testid="more-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-3 pb-2 space-y-1">
          {items.map((it) => (
            <Link key={it.to} to={it.to} onClick={onClose} data-testid={`more-${it.to.split("/").pop()}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-colors ${active(it.to) ? "bg-thali-green/10 text-thali-green" : "text-thali-ink/80 hover:bg-thali-cream"}`}>
              <it.icon className="w-5 h-5" /> {it.label}
              {it.badge && <span className="ml-auto text-[10px] font-extrabold bg-thali-saffron text-white rounded-full px-1.5 py-0.5">{it.badge}</span>}
            </Link>
          ))}
          <div className="flex items-center justify-between px-4 py-3 rounded-xl text-thali-ink/80">
            <span className="flex items-center gap-3 font-semibold"><Languages className="w-5 h-5" /> {t("language")}</span>
            <LanguageToggle />
          </div>
          <button onClick={() => { onClose(); onLogout(); }} data-testid="more-logout"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-semibold text-thali-ink/80 hover:bg-red-50 hover:text-red-600 transition-colors">
            <LogOut className="w-5 h-5" /> {t("logout")}
          </button>
        </div>
      </div>
    </div>
  );
}
